import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SectionTitle from "@/components/SectionTitle";
import ManualPaymentModal from "@/components/ManualPaymentModal";
import MyOrders from "@/components/MyOrders";
import { Crown, Check, Zap, Star, Clock } from "lucide-react";
import { motion } from "framer-motion";

const PLANS = [
  {
    id: "plan_trial",
    name: "FAS Trial",
    duration: "10 Hari",
    price: "Rp 69.000",
    amount: 69000,
    badge: "Coba Dulu",
    icon: Zap,
    features: ["Akses Briefing 08:15 WIB Pagi", "2 Rekomendasi Saham Harian", "3 Saham Scalping Volatil", "Semua Kalkulator Saham", "Analisis AI 950+ Saham", "Support Grup Telegram"],
  },
  {
    id: "plan_active",
    name: "FAS Active",
    duration: "30 Hari",
    price: "Rp 159.000",
    amount: 159000,
    badge: "Paling Populer",
    popular: true,
    icon: Star,
    features: ["Semua fitur FAS Trial", "Fitur Probabilitas Top Gainer", "Alert Realtime Rekomendasi", "Watchlist + Alert Harga Multi-Target", "Grafik Detail Candle + RSI + MACD"],
  },
  {
    id: "plan_pro",
    name: "FAS Pro",
    duration: "90 Hari",
    price: "Rp 399.000",
    amount: 399000,
    badge: "Paling Hemat",
    icon: Crown,
    features: ["Semua fitur FAS Active", "Akses VIP Bandarmology Data", "Konsultasi Portofolio 1-on-1", "Akses Arsip Briefing & E-book", "Prioritas Eksekusi Sinyal", "Hemat s/d 40% per hari"],
  },
];

const rupiah = (n) => "Rp " + Math.round(n).toLocaleString("id-ID");
const two = (n) => String(n).padStart(2, "0");

export default function MembershipSection() {
  const { user, login } = useAuth();
  const [loadingId, setLoadingId] = useState(null);
  const [payPlan, setPayPlan] = useState(null);
  const [ordersRefresh, setOrdersRefresh] = useState(0);
  const [now, setNow] = useState(Date.now());
  const trialActive = user?.membership_active && user?.membership_plan === "trial";
  const DISCOUNT = 0.2;

  useEffect(() => {
    if (!trialActive) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [trialActive]);

  const discountLeft = () => {
    const diff = Math.max(0, new Date(user.membership_expires) - now);
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return (d > 0 ? `${d}h ` : "") + `${two(h)}:${two(m)}:${two(s)}`;
  };

  const checkout = async (planId) => {
    if (!user) {
      toast.error("Silakan masuk terlebih dahulu untuk berlangganan.");
      login();
      return;
    }
    setPayPlan(planId);
  };

  return (
    <section id="membership" className="scroll-mt-20">
      <SectionTitle
        eyebrow="Bergabung Sekarang"
        title="Paket Membership FAS Trading"
        desc="Buka seluruh sinyal, briefing pagi, dan fitur probabilitas VIP. Pilih durasi yang sesuai gaya trading Anda."
        icon={Crown}
      />
      {user?.membership_active && (
        <div className="card p-4 mb-6 border-[var(--gold)]/30 bg-[var(--gold)]/5 flex items-center gap-3" data-testid="active-membership-banner">
          <Crown size={20} className="text-gold" />
          {user.membership_plan === "admin" ? (
            <p className="text-sm">Akun <span className="font-semibold text-gold">Admin</span> · akses penuh semua fitur tanpa batas waktu.</p>
          ) : user.membership_plan === "trial" ? (
            <p className="text-sm"><span className="font-semibold text-gold">Trial gratis</span> Anda aktif (<span data-testid="membership-days-left">{Math.max(1, Math.ceil((new Date(user.membership_expires) - Date.now()) / 86400000))} hari</span> tersisa). <span className="font-semibold text-accent">Upgrade sekarang hemat 20%</span> — berlaku selama trial masih aktif.</p>
          ) : (
            <p className="text-sm">Membership Anda <span className="font-semibold text-gold">aktif</span>{user.membership_expires ? <> · sisa <span className="font-semibold text-gold" data-testid="membership-days-left">{Math.max(1, Math.ceil((new Date(user.membership_expires) - Date.now()) / 86400000))} hari</span> (kedaluwarsa {new Date(user.membership_expires).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })})</> : ""}. Terima kasih telah bergabung dengan FAS Trading!</p>
          )}
        </div>
      )}
      <MyOrders refreshKey={ordersRefresh} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {PLANS.map((p) => (
          <motion.div
            key={p.id}
            whileHover={{ y: -4 }}
            transition={{ duration: 0.15 }}
            data-testid={`plan-card-${p.id}`}
            className={`card p-6 relative flex flex-col ${p.popular ? "border-[var(--accent)] ring-1 ring-[var(--accent)]/40" : ""}`}
          >
            {p.popular && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[var(--accent)] text-[#07090E] text-[11px] font-bold">PALING POPULER</span>
            )}
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${p.popular ? "bg-[var(--accent)]/15" : "bg-[var(--surface-hover)]"}`}>
                <p.icon size={18} className={p.popular ? "text-accent" : "text-[var(--text-secondary)]"} />
              </div>
              <span className="text-xs text-[var(--text-muted)] uppercase tracking-wider">{p.badge}</span>
            </div>
            <p className="font-heading text-2xl font-extrabold mt-2">{p.name}</p>
            <p className="text-sm text-[var(--text-secondary)]">{p.duration} akses penuh</p>
            {trialActive ? (
              <div className="mt-2" data-testid={`price-${p.id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[var(--text-muted)] line-through">{p.price}</span>
                  <span className="px-2 py-0.5 rounded-md bg-[var(--accent)]/15 text-accent text-[11px] font-bold" data-testid={`discount-badge-${p.id}`}>-20%</span>
                </div>
                <p className="font-heading text-3xl font-extrabold text-accent">{rupiah(p.amount * (1 - DISCOUNT))}</p>
                <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[var(--danger)]/10 text-[var(--danger)] text-[11px] font-semibold" data-testid={`discount-countdown-${p.id}`}>
                  <Clock size={11} /> Diskon berakhir dalam <span className="font-mono-data font-bold">{discountLeft()}</span>
                </div>
              </div>
            ) : (
              <p className="font-heading text-3xl font-extrabold text-accent mt-2" data-testid={`price-${p.id}`}>{p.price}</p>
            )}
            <div className="h-px bg-[var(--border)] my-5" />
            <ul className="space-y-2.5 flex-1">
              {p.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
                  <Check size={15} className="text-accent shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => checkout(p.id)}
              disabled={loadingId === p.id}
              data-testid={`subscribe-${p.id}`}
              className={`mt-6 w-full h-11 rounded-lg font-semibold transition-all disabled:opacity-60 ${p.popular ? "bg-[var(--accent)] text-[#07090E] hover:bg-[var(--accent-hover)]" : "bg-[var(--surface-hover)] text-[var(--text-primary)] hover:bg-[var(--border)] border border-[var(--border)]"}`}
            >
              {loadingId === p.id ? "Memproses..." : trialActive ? "Upgrade · Hemat 20%" : "Pilih Paket"}
            </button>
          </motion.div>
        ))}
      </div>
      <p className="text-xs text-[var(--text-muted)] mt-4 text-center max-w-2xl mx-auto">Pembayaran via <span className="text-accent font-semibold">Transfer Bank BRI</span> (verifikasi manual oleh admin, aktif setelah disetujui). Metode otomatis (QRIS/VA) menyusul.</p>
      <ManualPaymentModal planId={payPlan} onClose={() => { setPayPlan(null); setOrdersRefresh((k) => k + 1); }} />
    </section>
  );
}
